import { AxiosProgressEvent } from 'axios'
import { ReactNode } from 'react'
import invariant from 'tiny-invariant'

import FcaSearchLink from '@/components/misc/FcaSearchLink'
import { API_PATH_INTERNAL } from '@/lib/constants'
import apiService from '@/lib/services/apiService'
import userService from '@/lib/services/userService'
import { CompanyEntity } from '@/types/entities'
import { INTEGRATION_SERVICE, USER_TYPE } from '@/types/misc'
import { CreateCompanyBankDetailsRequest } from '@/types/requests/clients'
import {
  CompanyRequestCallRequest,
  CreateCompanyRequest,
  CreateCompanySelfReferralRequest,
  GetCompanyDetailsParams,
  GetCompanyProfileParams,
  GetCompanyUsersParams,
  GetReferralFirmIndividualsParams,
  GetReferralFirmsQuery,
  GetShareableRoundRobinReferralLinksParams,
  UpdateAdminDetailsRequest,
  UpdateClientReferralSettingsRequest,
  UpdateCompanyRequest,
  UpdateCompanySmtpDetailRequest,
  UpdateIfaDetailsRequest,
  UpdateMinInvestableAssetsRequest,
  UpdatePanelApprovalRequiredRequest,
  UpdateScheduleCallUrlRequest,
  UploadCompanyLogoRequest,
} from '@/types/requests/companies'
import { GetCompassAdviserStatsParams } from '@/types/requests/compass'
import { CompanyTypeItem } from '@/types/responses/common-data'
import {
  AdminCompanyUserCollection,
  AvailableServiceAreasCollection,
  CheckEnqueuedCompaniesResponse,
  ClientReferralSettingMetadataItem,
  CompanyAdminDetailItem,
  CompanyAvailableRecommendedPartnerCollection,
  CompanyBankDetailsItem,
  CompanyCollectionItem,
  CompanyCompassContentItem,
  CompanyDetailsItem,
  CompanyItem,
  CompanyPanelApprovalRequiredItem,
  CompanyProfileItem,
  CompanyRequestCallResponse,
  CompanyServiceCollection,
  CompanySmtpDetailItem,
  CompanyUserCollection,
  CompassPdfGuideDataItem,
  CreateCompanyResponse,
  CreateCompanySelfReferralResponse,
  GenerateClientReferralLinkFormMetadata,
  GlobalSearchResultsItem,
  NylasCalendarIntegrationCollection,
  NylasEmailIntegrationCollection,
  ReferralFirmCollection,
  ReferralFirmIndividualsCollection,
  ReferringCompanyItem,
  ShareableRoundRobinReferralLinkCollection,
  UpdateCompanyLogoResponse,
  UpdateCompanyScheduleCallUrlsResponse,
} from '@/types/responses/companies'
import {
  CompassAdviserStatsCollection,
  CompassAdviserStatsSummary,
} from '@/types/responses/compass'
import { ReferralStatsItem } from '@/types/responses/referral-stats'
import { ReferralStatusCollection } from '@/types/responses/referral-status'

class CompanyService {
  private endpoint = `${API_PATH_INTERNAL}/companies`

  createIri(companyId: number | string) {
    return `${this.endpoint}/${companyId}`
  }

  async getCompany(companyId: number): Promise<CompanyItem> {
    const response = await apiService.get(`${this.endpoint}/${companyId}`)

    return response.data
  }

  getIri(companyId: number): IRI {
    return `${this.endpoint}/${companyId}`
  }

  async getCompanyDetails(
    params: GetCompanyDetailsParams,
  ): Promise<CompanyDetailsItem> {
    const response = await apiService.get(`${this.endpoint}/details`, {
      params,
    })

    return response.data
  }

  async getUsers(
    companyId: number,
    params: GetCompanyUsersParams,
  ): Promise<CompanyUserCollection> {
    const response = await apiService.get(
      `${this.endpoint}/${companyId}/users`,
      { params },
    )

    return response.data
  }

  async getCompanyServices(
    companyId: number,
  ): Promise<CompanyServiceCollection> {
    const response = await apiService.get(
      `${this.endpoint}/${companyId}/services`,
    )

    return response.data
  }

  async getAvailableServiceAreas(
    companyId: number,
  ): Promise<AvailableServiceAreasCollection> {
    const response = await apiService.get(
      `${this.endpoint}/${companyId}/available-service-areas`,
    )

    return response.data
  }

  async getReferralStats(companyId: number): Promise<ReferralStatsItem> {
    const response = await apiService.get<ReferralStatsItem>(
      `${this.endpoint}/${companyId}/referral-stats`,
    )

    return response.data
  }

  async getReferralStatuses(
    companyId: number,
  ): Promise<ReferralStatusCollection> {
    const response = await apiService.get<ReferralStatusCollection>(
      `${this.endpoint}/${companyId}/referral-statuses`,
    )

    return response.data
  }

  async getCompanyByReferralCode(
    referralCode: string,
  ): Promise<ReferringCompanyItem> {
    const response = await apiService.get(
      `${this.endpoint}/${referralCode}/referring-company`,
    )

    return response.data
  }

  async getCompanyReferralFirms(
    currentCompanyId: number,
    params: GetReferralFirmsQuery,
  ): Promise<ReferralFirmCollection> {
    const response = await apiService.get(
      `${this.endpoint}/${currentCompanyId}/referral-firms`,
      {
        params: { ...params, status: 'all' },
      },
    )

    return response.data
  }

  async getReferralFirmIndividuals(
    referralFirmId: number,
    params?: GetReferralFirmIndividualsParams,
  ): Promise<ReferralFirmIndividualsCollection> {
    const response = await apiService.get(
      `${this.endpoint}/${referralFirmId}/referral-firm-individuals`,
      { params },
    )

    return response.data
  }

  async checkEnqueuedCompanies(
    indexIds: string[],
  ): Promise<CheckEnqueuedCompaniesResponse> {
    const response = await apiService.get(
      `${this.endpoint}/enqueued-companies`,
      { params: { indexIds } },
    )

    return response.data
  }

  async createCompanyAndUpdateContext(
    userId: number,
    request: CreateCompanyRequest,
  ): Promise<CreateCompanyResponse> {
    const createCompanyResponse = await apiService.post<CreateCompanyResponse>(
      this.endpoint,
      request,
    )

    await userService.updateUserContext(userId, {
      group: null,
      company: createCompanyResponse.data['@id'],
      scope: USER_TYPE.company,
    })

    return createCompanyResponse.data
  }

  async completeRegistration(companyId: number): Promise<unknown> {
    const response = await apiService.patch(
      `${this.endpoint}/${companyId}/complete-registration`,
      {},
    )

    return response.data
  }

  async requestAccess(companyId: number): Promise<unknown> {
    const response = await apiService.post(
      `${this.endpoint}/${companyId}/request-access`,
      {},
    )

    return response.data
  }

  async getCompassConfig(
    companyId: number,
  ): Promise<CompanyCompassContentItem> {
    const response = await apiService.get<CompanyCompassContentItem>(
      `${this.endpoint}/${companyId}/compass-config`,
    )

    const { heroIntroText, sectionOneIntroText } = response.data

    invariant(heroIntroText, 'Expected heroIntroText to be defined')
    invariant(sectionOneIntroText, 'Expected sectionOneIntroText to be defined')

    return response.data
  }

  async getCompassPdfGuideData(
    companyId: number,
  ): Promise<CompassPdfGuideDataItem> {
    const response = await apiService.get(
      `${this.endpoint}/${companyId}/compass-pdf-guide-data`,
    )

    return response.data
  }

  async getCompassAdviserStats(
    companyId: number,
    params: GetCompassAdviserStatsParams,
  ): Promise<CompassAdviserStatsCollection> {
    const response = await apiService.get<CompassAdviserStatsCollection>(
      `${this.endpoint}/${companyId}/compass-adviser-stats`,
      { params },
    )

    return response.data
  }

  async getCompassAdviserStatsSummary(
    companyId: number,
  ): Promise<CompassAdviserStatsSummary> {
    const response = await apiService.get<CompassAdviserStatsSummary>(
      `${this.endpoint}/${companyId}/compass-adviser-stats-summary`,
    )

    return response.data
  }

  async downloadCompassPdfGuide(companyId: number): Promise<{
    downloadUrl: string
  }> {
    const response = await apiService.post(
      `${this.endpoint}/${companyId}/compass-pdf-guide`,
      {},
    )

    return response.data
  }

  async submitRqCertifiedFeePaymentClaim(companyId: number): Promise<{
    downloadUrl: string
  }> {
    const response = await apiService.patch(
      `${this.endpoint}/${companyId}/rq-certified-fee-payment-claim`,
      {},
    )
    return response.data
  }

  async updateScheduleCallUrls(
    companyId: number,
    request: UpdateScheduleCallUrlRequest,
  ): Promise<UpdateCompanyScheduleCallUrlsResponse> {
    const response = await apiService.patch(
      `${this.endpoint}/${companyId}/schedule-call-url`,
      request,
    )

    return response.data
  }

  async updatePanelApprovalRequired(
    companyId: number,
    request: UpdatePanelApprovalRequiredRequest,
  ): Promise<CompanyPanelApprovalRequiredItem> {
    const response = await apiService.patch(
      `${this.endpoint}/${companyId}/panel-approval-required`,
      request,
    )

    return response.data
  }

  async createCompanySelfReferral(
    companyId: number,
    request: CreateCompanySelfReferralRequest,
  ): Promise<CreateCompanySelfReferralResponse> {
    const response = await apiService.post(
      `${this.endpoint}/${companyId}/self-referral`,
      request,
    )

    return response.data
  }

  async createCompanyRequestCallRequest(
    companyId: number,
    request: CompanyRequestCallRequest,
  ): Promise<CompanyRequestCallResponse> {
    const response = await apiService.post(
      `${this.endpoint}/${companyId}/request-call`,
      request,
    )

    return response.data
  }

  async getCompanyProfile(
    params: GetCompanyProfileParams,
  ): Promise<CompanyProfileItem> {
    const response = await apiService.get(`${this.endpoint}/profile`, {
      params,
    })

    return response.data
  }

  getInvitationLink(
    companyReferralCode: CompanyEntity['referralCode'],
  ): string {
    if (!companyReferralCode) {
      throw new Error(
        'Attempted to get an invitation link without a referral code ' +
          'being set for a company. All companies should have a referral code.',
      )
    }

    return `${window.location.origin}/invitation/${companyReferralCode}`
  }

  async getBankDetails(companyId: number): Promise<CompanyBankDetailsItem> {
    const response = await apiService.get<CompanyBankDetailsItem>(
      `${this.endpoint}/${companyId}/bank-details`,
    )

    return response.data
  }

  async submitBankDetails(
    companyId: number,
    request: CreateCompanyBankDetailsRequest,
  ): Promise<CompanyBankDetailsItem> {
    const response = await apiService.post<CompanyBankDetailsItem>(
      `${this.endpoint}/${companyId}/bank-details`,
      request,
    )

    return response.data
  }

  // TODO: Move logic to API
  getDpbLicenceMessage(
    company: CompanyCollectionItem,
    companyType: CompanyTypeItem,
  ): string | ReactNode {
    const foundDpbLicence = !!company.dpbReference
    const isDpbMember = company.isDpbMember
    const couldSelectRegulator = companyType.canSelectRegulatorAtSignUp
    const didSelectDpbRegulator = company.isDpbMember
    const hasRestrictedRegulator = company.hasRestrictedRegulator
    const dpbHref = <FcaSearchLink query={company.dpbReference} />

    // Don't show DPB messaging for regulators that aren't restricted
    if (!hasRestrictedRegulator) {
      return null
    }

    // Has licence and is DPB member or has licence but couldn't select regulator
    // at sign-up
    if (foundDpbLicence && (isDpbMember || !couldSelectRegulator)) {
      return <>It looks like your firm has a DPB licence ({dpbHref}).</>
    }

    // Has licence but no DPB regulator selected at sign-up
    if (foundDpbLicence && couldSelectRegulator && !didSelectDpbRegulator) {
      return (
        <>
          It looks like your firm has a DPB licence ({dpbHref}), however a
          professional body regulator was not selected from the list at sign up.
        </>
      )
    }

    // No licence found though the company selected a DPB regulator at sign-up
    if (!foundDpbLicence && couldSelectRegulator && didSelectDpbRegulator) {
      return "It looks like your firm doesn't have a DPB licence."
    }

    // No message to show
    return ''
  }

  async uploadLogo(
    companyId: number,
    request: UploadCompanyLogoRequest,
  ): Promise<UpdateCompanyLogoResponse> {
    const response = await apiService.post(
      `${this.endpoint}/${companyId}/upload-logo`,
      request,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )

    return response.data
  }

  async getSmtpDetail(companyId: number): Promise<CompanySmtpDetailItem> {
    const response = await apiService.get<CompanySmtpDetailItem>(
      `${this.endpoint}/${companyId}/email-integration`,
    )

    return response.data
  }

  async uploadVideo(
    companyId: number,
    videoFile: File,
    onUploadProgress: (data: AxiosProgressEvent) => void,
  ) {
    const response = await apiService.post(
      `${API_PATH_INTERNAL}/companies/${companyId}/upload-video`,
      { videoFile: videoFile },
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress,
      },
    )

    return response.data
  }

  async updateSmtpDetail(
    companyId: number,
    request: UpdateCompanySmtpDetailRequest,
  ): Promise<CompanySmtpDetailItem> {
    const response = await apiService.patch<CompanySmtpDetailItem>(
      `${this.endpoint}/${companyId}/email-integration`,
      request,
    )

    return response.data
  }

  // Change fields that are limited to just admin users
  async updateAdminDetails(
    companyId: number,
    request: UpdateAdminDetailsRequest,
  ): Promise<CompanyAdminDetailItem> {
    const response = await apiService.patch(
      `${this.endpoint}/${companyId}`,
      request,
    )

    return response.data
  }

  async updateOtherServices(companyId: number, otherServices: string) {
    const response = await apiService.patch(`${this.endpoint}/${companyId}`, {
      otherServices,
    })

    return response.data
  }

  async updateIfaDetails(
    companyId: number,
    request: UpdateIfaDetailsRequest,
  ): Promise<unknown> {
    const response = await apiService.patch(`${this.endpoint}/${companyId}`, {
      companyDetail: {
        ifa: request,
      },
    })

    return response.data
  }

  async getAvailableRecommendedPartners(
    companyId: number,
  ): Promise<CompanyAvailableRecommendedPartnerCollection> {
    const response = await apiService.get(
      `${this.endpoint}/${companyId}/available-recommended-partners`,
    )

    return response.data
  }

  async getShareableReferralLinks(
    companyId: number,
    params: GetShareableRoundRobinReferralLinksParams,
  ): Promise<ShareableRoundRobinReferralLinkCollection> {
    const response = await apiService.get(
      `${this.endpoint}/${companyId}/shareable-referral-links`,
      { params },
    )

    return response.data
  }

  async updateMinInvestableAssets(
    companyId: number,
    request: UpdateMinInvestableAssetsRequest,
  ) {
    const response = await apiService.patch(
      `${this.endpoint}/${companyId}/min-investable-assets`,
      request,
    )

    return response.data
  }

  async getCompanyNylasEmailIntegrations(): Promise<NylasEmailIntegrationCollection> {
    const response = await apiService.get(
      `${this.endpoint}/company-user-integrations/${INTEGRATION_SERVICE.nylasCalendar}`,
    )

    return response.data
  }

  async getCompanyNylasCalendarIntegrations(): Promise<NylasCalendarIntegrationCollection> {
    const response = await apiService.get(
      `${this.endpoint}/company-user-integrations/${INTEGRATION_SERVICE.nylasCalendar}`,
    )

    return response.data
  }

  async globalSearch(searchTerm: string): Promise<GlobalSearchResultsItem> {
    const response = await apiService.get(`${this.endpoint}/global-search`, {
      params: { searchTerm },
    })

    return response.data
  }

  async updateCompany(companyId: number, request: UpdateCompanyRequest) {
    const response = await apiService.patch(
      `${this.endpoint}/${companyId}`,
      request,
    )

    return response.data
  }

  async getAdminCompanyUsers(
    companyId: number,
  ): Promise<AdminCompanyUserCollection> {
    const response = await apiService.get(
      `${this.endpoint}/${companyId}/admins`,
    )

    return response.data
  }

  async getGenerateClientReferralLinkFormMetadata(
    companyReferralCode: string,
  ): Promise<GenerateClientReferralLinkFormMetadata> {
    const response = await apiService.get(
      `${this.endpoint}/${companyReferralCode}/generate-client-referral-link-form-metadata`,
    )

    return response.data
  }

  async getClientReferralSettingsMetadata(
    companyId: number,
  ): Promise<ClientReferralSettingMetadataItem> {
    const response = await apiService.get(
      `${this.endpoint}/${companyId}/client-referral-settings-metadata`,
    )

    return response.data
  }

  async updateClientReferralSettings(
    companyId: number,
    request: UpdateClientReferralSettingsRequest,
  ) {
    const response = await apiService.patch(
      `${this.endpoint}/${companyId}/client-referral-settings`,
      request,
    )

    return response.data
  }
}

const companyService = new CompanyService()

export default companyService
